import { useEffect, useState } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { auth } from '../firebase';
import { getSessionConfig, getMe, updateMe } from '../lib/api';
import { useUserStore } from '../store/userStore';
import { queryClient } from '../lib/queryClient';
import { useActionStore } from '../store/actionStore';
import { setTelemetryUser } from '../lib/telemetry';
import type { SessionConfig } from '../types';

interface UseAuthReturn {
  user: User | null;
  loading: boolean;
  pendingClaims: boolean;
  sessionConfig: SessionConfig | null;
}

/**
 * "entityId:userId" fingerprint of a session, or null when there is no
 * authenticated identity. Used to detect an identity change (sign-out,
 * sign-in as a different account, or a restored session with different
 * claims) as distinct from a same-identity event like a silent token
 * refresh — same discipline as userStore.setSessionConfig's activeRole
 * reseed logic.
 */
function identityFingerprint(config: SessionConfig | null): string | null {
  return config ? `${config.entityId}:${config.userId}` : null;
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingClaims, setPendingClaims] = useState(false);
  const { sessionConfig, setSessionConfig, setUser: setStoreUser, logout } = useUserStore();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      // Snapshot the identity that was active BEFORE this auth event, read
      // directly from the store rather than the reactive `sessionConfig`
      // above (which is fixed at effect-creation time and would go stale
      // across repeated auth events on this same subscription).
      const prevIdentity = identityFingerprint(useUserStore.getState().sessionConfig);

      if (firebaseUser) {
        try {
          // Force token refresh so newly-set custom claims (entityId, entityRole) are visible
          await firebaseUser.getIdToken(true);
          const config = await getSessionConfig();

          // Cross-tenant/cross-account client-state isolation: if the
          // resolved identity differs from whatever was previously active
          // (sign-in as a different user/entity, or a session restored with
          // different claims after sign-out never fired an intermediate
          // null tick), wipe identity-scoped client state before any
          // component can read stale, wrongly-scoped data out of it. A
          // same-identity tick (silent token refresh) leaves the
          // fingerprint unchanged and is deliberately a no-op — losing
          // completed actions on every silent refresh would be a
          // regression, not a fix.
          if (identityFingerprint(config) !== prevIdentity) {
            queryClient.clear();
            useActionStore.getState().clearActions();
            setTelemetryUser(config.userId, config.entityRole ?? null);
          }

          setPendingClaims(false);
          setSessionConfig(config);

          // Load user profile and sync Google OAuth data if missing
          try {
            const profile = await getMe();
            const syncPayload: Record<string, string> = {};
            if (firebaseUser.displayName && !profile.fullName) {
              syncPayload.fullName = firebaseUser.displayName;
            }
            // Provider-derived avatar (Google, via Firebase Auth's photoURL) only
            // syncs in when the user hasn't manually set one in Settings — a
            // manual pick (avatarSource === 'manual') always wins and is never
            // overwritten here. This is the single source of truth for avatar
            // precedence; keep it in sync with the write in MySettings.tsx.
            if (
              firebaseUser.photoURL &&
              profile.avatarSource !== 'manual' &&
              profile.profilePictureUrl !== firebaseUser.photoURL
            ) {
              syncPayload.profilePictureUrl = firebaseUser.photoURL;
              syncPayload.avatarSource = 'google';
            }
            if (Object.keys(syncPayload).length > 0) {
              const updated = await updateMe(syncPayload);
              setStoreUser(updated);
            } else {
              setStoreUser(profile);
            }
          } catch {
            // Profile load failure is non-fatal — sidebar falls back to sessionConfig
          }
        } catch (err: unknown) {
          // A 403 means the user is authenticated but custom claims are not set yet.
          // This happens for Google sign-ins with no entity membership, or immediately
          // after sign-up before invite acceptance completes.
          const status = (err as Error & { status?: number }).status;
          if (status === 403) {
            setPendingClaims(true);
          } else {
            setPendingClaims(false);
          }
          // No valid identity was resolved — clear any previous identity's
          // cache and client state rather than leaving it readable under the
          // pending-claims screen or a subsequent different sign-in.
          if (prevIdentity !== null) {
            queryClient.clear();
            useActionStore.getState().clearActions();
            setTelemetryUser(null, null);
          }
          setSessionConfig(null);
        }
      } else {
        // Sign-out. Also covers the common case that doesn't rely on the
        // sign-in branch's comparison above.
        if (prevIdentity !== null) {
          queryClient.clear();
          useActionStore.getState().clearActions();
          setTelemetryUser(null, null);
        }
        setPendingClaims(false);
        logout();
      }

      setLoading(false);
    });

    return unsubscribe;
  }, [setSessionConfig, setStoreUser, logout]);

  return { user, loading, pendingClaims, sessionConfig };
}
